import { useQuery } from "@apollo/client";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import DimensionsModal from "../../../../../../../../../components/Modals/DimensionsModal";
import { FETCH_ARTWORK_CATEGORIES } from "../../../../../../../../../graphql/query/ArtworkCategory";
import { FETCH_ARTWORK_TARIFFS } from "../../../../../../../../../graphql/query/ArtworkTariff";
import useTooltip from "../../../../../../../../../hooks/useTooltip";
import useUser from "../../../../../../../../../hooks/useUser";
import isUserFieldChange from "../../../../../../../../../util/isUserFieldChange";
import InstanceInputRender from "./InstanceInputRender";
import { formatWeight, getPackedWeightBreakdown } from "./packedWeightBreakdown";

const PackedWeightTooltip = ({ breakdown, position }) =>
  createPortal(
    <div
      className="pointer-events-none fixed z-50 w-72 rounded-md border border-gray-200 bg-white p-3 text-xs text-gray-800 shadow-xl"
      style={{ top: position.top, left: position.left }}
    >
      <div className="mb-2 font-bold">Packed weight breakdown</div>
      {breakdown.rows.length > 0 ? (
        <ul className="space-y-1">
          {breakdown.rows.map(row => (
            <li key={row.id} className="flex justify-between gap-3">
              <span className="min-w-0 truncate">
                #{row.id} {row.category}
                {row.description ? ` – ${row.description}` : ""}
                {row.isPacking ? (
                  <span className="text-gray-400"> (packing)</span>
                ) : null}
              </span>
              <span className="shrink-0 font-medium">
                {formatWeight(row.weight)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-gray-400">No warehouse items</div>
      )}
      <div className="mt-2 space-y-1 border-t border-gray-200 pt-2">
        <div className="flex justify-between">
          <span>Weight</span>
          <span>{formatWeight(breakdown.weight)}</span>
        </div>
        <div className="flex justify-between">
          <span>Packing</span>
          <span>{formatWeight(breakdown.packingWeight)}</span>
        </div>
        <div className="flex justify-between font-bold">
          <span>Packed weight</span>
          <span>{formatWeight(breakdown.packedWeight)}</span>
        </div>
      </div>
    </div>,
    document.body,
  );

// Marking an instance fabricated stamps today's date; un-marking clears it.
// Only a user's toggle does this, so loading a record never rewrites it.
export const getFabricatedDateUpdate = ({
  fabricated,
  fabricatedDate,
  previousFabricated,
  today,
}) => {
  if (!isUserFieldChange(previousFabricated, fabricated)) return undefined;
  if (fabricated === -1 && !fabricatedDate) return today;
  if (fabricated === 0 && fabricatedDate) return null;
  return undefined;
};

const InstanceDetailCol2 = ({ formik, isDisabled, submitOnBlur = false }) => {
  const { hasPermission } = useUser();
  const { data: { artworkCategories = [] } = {} } = useQuery(
    FETCH_ARTWORK_CATEGORIES,
    { fetchPolicy: "cache-first" },
  );
  const { data: { artworkTariffs = [] } = {} } = useQuery(
    FETCH_ARTWORK_TARIFFS,
    { fetchPolicy: "cache-first" },
  );

  const { showTooltip, tooltipPos, handleMouseEnter, handleMouseLeave } =
    useTooltip({ hideDelay: 50 });
  const packedWeightBreakdown = getPackedWeightBreakdown(
    formik?.values?.warehouseItem,
  );

  const fabricated = formik?.values?.fabricated;
  const previousFabricatedRef = useRef(fabricated);

  useEffect(() => {
    const previousFabricated = previousFabricatedRef.current;
    previousFabricatedRef.current = fabricated;
    const fabricatedDate = getFabricatedDateUpdate({
      fabricated,
      fabricatedDate: formik?.values?.fabricatedDate,
      previousFabricated,
      today: new Date(),
    });
    if (fabricatedDate !== undefined) {
      formik?.setFieldValue("fabricatedDate", fabricatedDate);
    }
  }, [fabricated]);

  const inputs = [
    {
      label: "Dimensions",
      name: "edition.artwork.dimensions",
      type: "textarea",
      inputClassName: "pr-7",
      rows: 5,
      modalButton: () => (
        <DimensionsModal
          formik={formik}
          submitOnBlur={submitOnBlur}
          fieldName={"edition.artwork.dimensions"}
          disabled={isDisabled || !hasPermission("UPDATE_ARTWORK_DIMENSIONS")}
        />
      ),
      disabled: isDisabled,
      submitOnBlur,
    },
    {
      name: "edition.artwork.series",
      label: "Series",
      type: "text",
      disabled: isDisabled,
      submitOnBlur,
    },
    {
      name: "edition.artwork.categoryId",
      label: "Category",
      type: "select",
      options: artworkCategories?.map(cat => ({
        label: cat.name,
        value: cat.id,
      })),
      disabled: isDisabled,
      submitOnBlur,
    },
    {
      name: "edition.artwork.goodsCode",
      label: "Goods Code",
      type: "select",
      disabled: isDisabled,
      submitOnBlur,
      options: artworkTariffs?.map(tariff => ({
        label: tariff?.code,
        value: tariff?.code,
      })),
    },
    {
      name: "unpackedWeight",
      label: "Weight (Kg)",
      disabled: true,
      submitOnBlur,
    },
    {
      name: "weight",
      label: "Packed Weight (Kg)",
      disabled: true,
      inputContainerClassName: "!border-gray-400",
      postIcon: (
        <InformationCircleIcon
          aria-label="Show packed weight breakdown"
          className="h-4 w-4 cursor-pointer text-gray-400"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      ),
      submitOnBlur,
    },
    {
      name: "condition.status.description",
      label: "Condition",
      type: "text",
      disabled: true,
      submitOnBlur,
    },
    {
      name: "fabricatedDate",
      label: "Fabricated",
      placeholder: "DD/MM/YYYY",
      type: "date",
      checkbox: {
        disabled: isDisabled,
        name: "fabricated",
        options: [{ label: "AP", value: -1 }],
        // disabled: !hasPermission("UPDATE_INSTANCE_FABRICATED"),
        submitOnBlur,
      },
      disabled: true,
      submitOnBlur,
    },
    {
      name: "framed",
      label: "Framed",
      type: "toggle",
      designType: "threeStateCheckbox",
      disabled: isDisabled || !hasPermission("UPDATE_INSTANCE_FRAMED"),
      submitOnBlur,
    },
  ];

  return (
    <div className={`grid h-fit grid-cols-1 gap-4`}>
      <InstanceInputRender inputs={inputs} formik={formik} />
      {showTooltip && tooltipPos && (
        <PackedWeightTooltip
          breakdown={packedWeightBreakdown}
          position={tooltipPos}
        />
      )}
    </div>
  );
};

export default InstanceDetailCol2;
